/**
 * Специальная ошибка, обозначающая отмену debounced-вызова.
 * Её можно безопасно игнорировать в обработчиках или фильтровать в Sentry.
 */
export class DebounceCanceledError extends Error {
  constructor() {
    super('Debounced call was canceled');
    this.name = 'DebounceCanceledError';
  }
}

/**
 * Вспомогательная функция для создания асинхронного debounce.
 * Предотвращает частые вызовы функции за короткий интервал времени.
 *
 * @param fn - Функция, которую нужно обернуть в debounce.
 * @param delay - Задержка в миллисекундах.
 * @returns - Декорированная функция с дебаунсом.
 */
export default function debounceAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => Promise<ReturnType<T> extends Promise<infer R> ? R : never> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingPromiseReject: ((reason?: any) => void) | null = null;

  return function (
    this: ThisParameterType<T>,
    ...args: Parameters<T>
  ): Promise<ReturnType<T> extends Promise<infer R> ? R : never> {
    if (timer) {
      clearTimeout(timer);
      if (pendingPromiseReject) {
        pendingPromiseReject(new DebounceCanceledError());
      }
    }

    return new Promise<ReturnType<T> extends Promise<infer R> ? R : never>((
      resolve,
      reject,
    ) => {
      pendingPromiseReject = reject;
      timer = setTimeout(async () => {
        try {
          // Используем правильное this
          const result = await fn.apply(this, args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}
